import { ColorTokenKey } from '@faire/web-api--source/indigofair/data/ColorTokenKey';
import { IResponsiveFloat } from '@faire/web-api--source/indigofair/data/IResponsiveFloat';
import { IResponsiveSpacing } from '@faire/web-api--source/indigofair/data/IResponsiveSpacing';
import { Spacing } from '@faire/web-api--source/indigofair/data/Spacing';

export namespace ICarouselStyle {
  export enum CarouselTransitionStyle {
    CAROUSEL_TRANSITION_STYLE_UNKNOWN = 'CAROUSEL_TRANSITION_STYLE_UNKNOWN',
    /**
     * SNAP in desktop, SCROLL in mobile
     */
    DEFAULT_CAROUSEL_TRANSITION_STYLE = 'DEFAULT_CAROUSEL_TRANSITION_STYLE',
    /**
     * For the SNAP behaviour, when the user drags a slide to transition, the carousel will adjust the slide
     * such that the first tile in the slide aligns to the left edge of the tile container
     */
    SNAP = 'SNAP',
    /**
     * For the SCROLL behaviour, when the user drags a slide to transition, the carousel will act as though it is a
     * horizontal scrollable container and not attempt to align the slides within the tile container
     */
    SCROLL = 'SCROLL',
  }

  export enum NavigationControl {
    NAVIGATION_CONTROL_UNKNOWN = 'NAVIGATION_CONTROL_UNKNOWN',
    /**
     * For the NO_CONTROLS behaviour, no navigation controls will be shown and the user will only be able to
     * transition between slides using their pointer to drag a slide left or right
     */
    NO_CONTROLS = 'NO_CONTROLS',
    /**
     * For the ARROWS behaviour, navigation arrows will appear to the left and the right of the tile container
     * Upon interacting with the arrows, the carousel will transition to the previous or next slides. Upon transition,
     * the first tile of the target slide will be aligned left edge of the tile container.
     */
    ARROWS = 'ARROWS',
    /**
     * ARROWS with primary variant in desktop, NONE in mobile
     */
    DEFAULT_PRIMARY_NAVIGATION_CONTROL = 'DEFAULT_PRIMARY_NAVIGATION_CONTROL',
    /**
     * ARROWS with secondary variant in desktop, NONE in
     */
    DEFAULT_SECONDARY_NAVIGATION_CONTROL = 'DEFAULT_SECONDARY_NAVIGATION_CONTROL',
  }

  export enum LoopBehavior {
    LOOP_BEHAVIOR_UNKNOWN = 'LOOP_BEHAVIOR_UNKNOWN',
    /**
     * INFINITE in desktop, NO_LOOP in mobile
     */
    DEFAULT_LOOP_BEHAVIOR = 'DEFAULT_LOOP_BEHAVIOR',
    /**
     * For the NO_LOOP behaviour, when the user has transitioned through all slides to reach the last tile, the user
     * will no longer will able to drag or use navigation controls to view more slides. The last tile in the last slide
     * will be aligned to the right edge of the tile container
     */
    NO_LOOP = 'NO_LOOP',
    /**
     * For the INFINITE behaviour, when the user has transitioned through all slides to reach the last tile, the user
     * will able to drag or use navigation control to continue navigation. If the last slide, does not have enough tile
     * to items per slide, the first N tiles from the first slide will be appending to the end of the last slide to
     * to create a virtual full slide of tiles. Transition to the next slide from the last virtual slide will transition
     * to the first slide. The creates the effect of always being able to transition to the next / previous slide
     */
    INFINITE = 'INFINITE',
  }

  export namespace ITileWidth {
    /**
     * Return an instance of ITileWidth with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (partial?: Partial<ITileWidth>): ITileWidth =>
      ({
        ...(partial ?? {}),
      }) as ITileWidth;
  }

  export interface ITileWidth {
    /**
     * Tile width of fractional causes all tiles within a carousel slide to be a consistent fraction of the slide's
     * width. The fractional width is calculated by dividing the slide width by the responsiveTilesPerSlide property
     * defined in the FractionalWidth message.
     */
    fractional: ICarouselStyle.IFractionalTileWidth | undefined;
    /**
     * Content width tiles will cause the width of each tiles to be determined by it's content.
     * This is useful for carousels where the tiles width can vary greatly.
     */
    content: ICarouselStyle.ContentTileWidth | undefined;
  }

  export namespace IFractionalTileWidth {
    /**
     * Return an instance of IFractionalTileWidth with the provided fields. Any field not
     * listed will be populated with an empty value (an empty array,
     * or undefined, whichever is valid for the type of the value.)
     */
    export const build = (
      partial?: Partial<IFractionalTileWidth>
    ): IFractionalTileWidth =>
      ({
        ...(partial ?? {}),
      }) as IFractionalTileWidth;
  }

  export interface IFractionalTileWidth {
    /**
     * The number of tiles within a carousel slide.
     * Note that on mobile a "carousel slide" is considered the screen width *minus* both the start & end
     * padding. For example if we have a fractionalTileWidth = 2, carouselPadding = 16 and tileSpacing = 12,
     * we will show 2 tiles and peek 4 px of the next tile (carouselPadding - tileSpacing)
     * See comment on tileWidth property in CarouselStyles for more information.
     * In a decimal representation of the tiles per slide:
     * the integer-part (characteristic) represents the number of full tiles to be shown in the slide
     * whereas the decimal-part (mantissa) represent how much of the first item in the next slide (if present) will be shown
     */
    responsive_tiles_per_slide: IResponsiveFloat | undefined;
  }

  export type ContentTileWidth = unknown;

  /**
   * Return an instance of ICarouselStyle with the provided fields. Any field not
   * listed will be populated with an empty value (an empty array,
   * or undefined, whichever is valid for the type of the value.)
   */
  export const build = (partial?: Partial<ICarouselStyle>): ICarouselStyle =>
    ({
      ...(partial ?? {}),
    }) as ICarouselStyle;
}

export interface ICarouselStyle {
  /**
   * Deprecated, please use the generic Component.style field (ComponentStyle.backgroundColor) instead
   * @deprecated
   */
  background_color: keyof typeof ColorTokenKey | undefined;
  /**
   * Represents the horizontals spacing between tiles of the carousel component.
   */
  tile_spacing: keyof typeof Spacing | undefined;
  /**
   * Represents the way that a user dragging a carousel with their pointer causes the slides to transition.
   * Currently only supported in web clients as mobile app carousel only support SCROLL behaviour.
   */
  transition_style:
    | keyof typeof ICarouselStyle.CarouselTransitionStyle
    | undefined;
  /**
   * Represents whether navigation controls appear for the carousel.
   * Currently only supported in web clients as mobile app carousels only support NO_CONTROLS behaviour
   */
  navigation_control: keyof typeof ICarouselStyle.NavigationControl | undefined;
  /**
   * Represents the behaviour of the carousel when the user has transitioned to the last tile on the last slide.
   * Currently only supported in web clients as mobile app carousels only support NO_LOOP behaviour.
   */
  loop_behavior: keyof typeof ICarouselStyle.LoopBehavior | undefined;
  /**
   * Represents the heuristic that should be used to calculated a tile's width within a carousel slide
   * Currently, this property will only be supported in web clients, as mobile app carousels only support
   * ContentTileWidth behaviour.
   * If unprovided or an empty tileWidth, web clients will assume a default tileWidth fractional type
   * with the responsiveTilesPerSlide set as mobileAndAbove = 2.2f, tabletAndAbove = 3f, desktopAndAbove = 5f
   */
  tile_width: ICarouselStyle.ITileWidth | undefined;
  /**
   * Represents the horizontal spacing between tiles in the carousel component.
   */
  responsive_tile_spacing: IResponsiveSpacing | undefined;
}
